import { zodResolver } from '@hookform/resolvers/zod';
import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { z } from 'zod';
import type { CustomPortSetting, CustomPortSettingProtocol } from './model';

type ProtocolOption = { value: CustomPortSettingProtocol; label: string };

interface PortSettingProps {
  initialValues: CustomPortSetting;
  onClose: () => void;
  onSubmit: (setting: CustomPortSetting) => void;
  submitting?: boolean;
  type: 'add' | 'edit';
}

interface PortSettingFields {
  label: string;
  protocol: CustomPortSettingProtocol;
  port: string;
  subnets: IPSubnetInputValueOrDraft;
  excludeSubnets: IPSubnetInputValueOrDraft;
}

export const CustomPortSettingsDialog = ({
  type: formType,
  initialValues,
  onClose,
  onSubmit,
  submitting,
}: PortSettingProps) => {
  const { handleSubmit, control, formState } = useCustomForm<PortSettingFields>(
    {
      defaultValues: {
        ...initialValues,
        excludeSubnets: { ips: initialValues.excludeSubnets },
        label: initialValues.label ?? '',
        port: initialValues.port.toString(),
        subnets: { ips: initialValues.subnets },
      },
      resolver: zodResolver(getValidationSchema()),
    },
  );

  const handlePortSettingSubmit = (data: PortSettingFields) => {
    if (Number.isNaN(Number(data.port))) {
      throw new IpfError('GENERIC', 'Port must be a number');
    }

    const preparedData = {
      ...data,
      subnets: data.subnets.ips,
      excludeSubnets: data.excludeSubnets.ips,
      port: Number(data.port),
    };
    onSubmit(preparedData);
  };

  return (
    <SidePanel
      title={formType === 'add' ? 'Add new port' : 'Edit port'}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            loading={formState.isSubmitting || submitting}
            onClick={handleSubmit(handlePortSettingSubmit)}
            disabled={!formState.isDirty}
          >
            {formType === 'add' ? 'Add port' : 'Update port'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit(handlePortSettingSubmit)}>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField
            control={control}
            name='label'
            label='Label'
            type='text'
          />
          <FormSelect.Mandatory
            control={control}
            name='protocol'
            label='Protocol'
            options={
              [
                { value: 'ssh', label: 'SSH' },
                { value: 'telnet', label: 'Telnet' },
              ] satisfies ProtocolOption[]
            }
          />
          <FormTextField
            control={control}
            name='port'
            label='Port'
            type='number'
          />
          <FormIPSubnetField
            control={control}
            name='subnets'
            label='Included subnets'
            type='cidr'
            placeholder='Insert one or more subnets'
          />
          <FormIPSubnetField
            control={control}
            name='excludeSubnets'
            label='Excluded subnets'
            type='cidr'
            placeholder='Insert one or more subnets'
          />
        </Card>
      </form>
    </SidePanel>
  );
};

const getValidationSchema = () => {
  return (
    z
      .object({
        label: z.string().nonempty('Label is required'),
        protocol: z.enum(['ssh', 'telnet']),
        port: z.string().optional(),
        subnets: ipSubnetInputValueSchema,
        excludeSubnets: ipSubnetInputValueSchema.optional(),
      })
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: high complexity is acceptable for form validation
      .superRefine((data, ctx) => {
        const numberRegex = /^\d+$/;

        if (!data.port) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Port is required',
            path: ['port'],
          });
        }

        if (data.port && !numberRegex.test(data.port)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Port must be a number',
            path: ['port'],
          });
        }

        if (data.port && (Number(data.port) < 1 || Number(data.port) > 65535)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Port must be greater than 0 and less than 65536',
            path: ['port'],
          });
        }

        if (hasIPSubnetInputValueError(data.subnets)) {
          // Display the error message from the draft value
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.subnets.message,
            path: ['subnets'],
          });
        } else if (data.subnets.ips.length === 0) {
          // Validate that at least one subnet is provided
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one subnet is required',
            path: ['subnets'],
          });
        }

        if (
          data.excludeSubnets !== undefined &&
          hasIPSubnetInputValueError(data.excludeSubnets)
        ) {
          // Display the error message from the draft value
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.excludeSubnets.message,
            path: ['excludeSubnets'],
          });
        }
      })
  );
};
